import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { cn } from '@goaltree/ui/lib/utils'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import {
  ArrowLeft,
  ChevronRight,
  ClipboardList,
  Layers,
  Pin,
  Snowflake,
  Sparkles,
  UserRoundX,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppSidebar } from '@/components/app-sidebar'
import { AssistantBrief } from '@/components/coaching/assistant-brief'
import { AssistantDraft } from '@/components/coaching/assistant-draft'
import { AssistantTweaksDialog } from '@/components/coaching/assistant-tweaks'
import {
  CheckInDots,
  ProgressBar,
  StatusPill,
  useLastActive,
} from '@/components/coaching/coach-dashboard'
import { useSavePrivateTemplate } from '@/components/coaching/coach-library'
import { NotesThread } from '@/components/coaching/coach-notes'
import { toastCoachError } from '@/components/coaching/coach-utils'
import { GivePlanDialog } from '@/components/coaching/give-plan-dialog'
import { RolePill } from '@/components/coaching/role-pill'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { SiteHeader } from '@/components/site-header'
import { UserAvatar } from '@/components/user-avatar'
import { goalUrl } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/coaching/$clientId')({
  head: () => ({
    meta: [{ title: 'Client — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-right text-sm font-semibold">{children}</span>
    </div>
  )
}

function RouteComponent() {
  const { t, i18n } = useTranslation()
  const { clientId } = Route.useParams()
  const navigate = useNavigate()
  const detail = useQuery(api.coaching.clientDetail, { clientId })
  const me = useQuery(api.auth.getCurrentUser)
  const lastActive = useLastActive()
  const [thread, setThread] = useState<Id<'nodes'> | null>(null)
  const [confirmEnd, setConfirmEnd] = useState(false)
  const [givePlan, setGivePlan] = useState(false)
  const saveTemplate = useSavePrivateTemplate()
  const [tweakGoal, setTweakGoal] = useState<{
    _id: Id<'nodes'>
    title: string
    category: string
  } | null>(null)
  const end = useMutation(api.coaching.endCoaching).withOptimisticUpdate((store, { linkId }) => {
    store.setQuery(api.coaching.clientDetail, { clientId }, null)
    const list = store.getQuery(api.coaching.myClients, {})
    if (list)
      store.setQuery(
        api.coaching.myClients,
        {},
        list.filter((c) => c.linkId !== linkId),
      )
  })
  const hidden = <span className="font-normal text-muted-foreground">{t('coaching.hidden')}</span>

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden bg-background">
        <MobileScreenHeader
          title={detail?.client.name ?? t('coaching.title')}
          showInbox={false}
          onBack={() => navigate({ to: '/coaching' })}
        />
        <SiteHeader className="hidden lg:flex" />
        <div className="flex-1 overflow-y-auto px-4 pb-16 lg:pb-10">
          <div className="mx-auto max-w-(--page-col) pt-6 lg:pt-8">
            <Link
              to="/coaching"
              className="hidden items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground lg:inline-flex"
            >
              <ArrowLeft className="size-4" />
              {t('coaching.allClients')}
            </Link>

            {detail === undefined ? (
              <div className="mt-6 h-40 animate-pulse rounded-2xl bg-muted/40" />
            ) : detail === null ? (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <UserRoundX className="size-10 text-muted-foreground" strokeWidth={1.75} />
                <p className="font-semibold">{t('coaching.notCoaching')}</p>
                <Link to="/coaching" className="text-sm font-semibold text-primary-text">
                  {t('coaching.allClients')}
                </Link>
              </div>
            ) : (
              <>
                <div className="mt-4 flex items-center gap-3">
                  <UserAvatar
                    image={detail.client.image}
                    name={detail.client.name}
                    size={48}
                    className="size-12 rounded-full"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h1 className="truncate text-xl font-black tracking-tight">
                        {detail.client.name}
                      </h1>
                      <RolePill role="client" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('coaching.clientSince', {
                        date: new Date(detail.since).toLocaleDateString(i18n.language, {
                          month: 'short',
                          year: 'numeric',
                        }),
                      })}
                      {detail.week.streak !== null &&
                        ` · ${t('coaching.streak', { count: detail.week.streak })}`}
                    </p>
                  </div>
                  <StatusPill status={detail.week.status} />
                </div>

                <div className="mt-6 grid gap-5">
                  <AssistantBrief clientId={clientId} clientName={detail.client.name} />
                  <div className="space-y-5">
                    <section className="rounded-2xl border bg-card px-4 py-3">
                      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        {t('coaching.thisWeek')}
                      </p>
                      <div className="mt-1 divide-y">
                        <Stat label={t('coaching.checkIns')}>
                          {detail.week.checkIns === null ? (
                            hidden
                          ) : (
                            <span className="inline-flex items-center gap-2">
                              <CheckInDots count={detail.week.checkIns} />
                              {detail.week.checkIns}/7
                            </span>
                          )}
                        </Stat>
                        <Stat label={t('coaching.habitsHit')}>
                          {detail.week.habitsHit === null
                            ? hidden
                            : `${detail.week.habitsHit}/${detail.week.habitsDue}`}
                        </Stat>
                        <Stat label={t('coaching.tasksDone')}>{detail.week.tasksDone}</Stat>
                        <Stat label={t('coaching.lastActive')}>
                          {detail.week.lastActiveAt === null
                            ? hidden
                            : lastActive(detail.week.lastActiveAt)}
                        </Stat>
                        <Stat label={t('coaching.slipped')}>
                          {detail.week.slipped === null
                            ? hidden
                            : detail.week.slipped.length
                              ? detail.week.slipped.join(' · ')
                              : t('coaching.nothingSlipped')}
                        </Stat>
                      </div>
                    </section>

                    <section>
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                          {detail.scope === 'all'
                            ? t('coaching.sharedAll')
                            : t('coaching.sharedPicked')}
                        </p>
                        <button
                          type="button"
                          onClick={() => setGivePlan(true)}
                          className="inline-flex cursor-pointer items-center gap-1 text-xs font-semibold text-primary-text hover:underline"
                        >
                          <ClipboardList className="size-3.5" />
                          {t('coachPlans.give')}
                        </button>
                      </div>
                      <div className="overflow-hidden rounded-2xl border bg-card">
                        {detail.goals.length === 0 && (
                          <p className="px-4 py-4 text-sm text-muted-foreground">
                            {t('coaching.noGoals')}
                          </p>
                        )}
                        {detail.goals.map((g) => (
                          <div
                            key={g._id}
                            className="flex items-center border-t transition-colors first:border-t-0 hover:bg-muted/40"
                          >
                            <Link
                              to="/goals/$goalId"
                              params={{ goalId: goalUrl(g.title, g._id).replace('/goals/', '') }}
                              className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-4"
                            >
                              <div className="min-w-0 flex-1">
                                <p
                                  className={cn(
                                    'flex items-center gap-1.5 truncate text-sm font-semibold',
                                    g.done && 'text-muted-foreground line-through',
                                  )}
                                >
                                  {g.frozen && <Snowflake className="size-3.5 text-sky-400" />}
                                  <span className="truncate">{g.title}</span>
                                </p>
                                <ProgressBar value={g.progress} className="mt-1.5" />
                              </div>
                              <span className="font-mono text-xs text-muted-foreground">
                                {Math.round(g.progress)}%
                              </span>
                              <ChevronRight className="size-4 text-muted-foreground" />
                            </Link>
                            <button
                              type="button"
                              onClick={() => void saveTemplate(g._id)}
                              aria-label={t('coachLibrary.saveAsTemplate')}
                              title={t('coachLibrary.saveAsTemplate')}
                              className="ml-2 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                            >
                              <Layers className="size-4" />
                            </button>
                            {!g.done ? (
                              <button
                                type="button"
                                onClick={() => setTweakGoal(g)}
                                aria-label={t('coachAssistant.tweaks')}
                                title={t('coachAssistant.tweaks')}
                                className="mx-2 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-primary-text hover:bg-primary/10"
                              >
                                <Sparkles className="size-4" />
                              </button>
                            ) : (
                              <span className="w-4" />
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">{t('coaching.editHint')}</p>
                    </section>
                  </div>

                  <div className="space-y-5">
                    {detail.pinned && (
                      <section className="rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3">
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-primary-text">
                          <Pin className="size-3.5" />
                          {t('coaching.notes.focus')}
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-sm font-medium">
                          {detail.pinned.body}
                        </p>
                      </section>
                    )}
                    <section className="rounded-2xl border bg-card px-4 py-3">
                      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        {t('coaching.notes.title')}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {[{ _id: null, title: t('coaching.notes.general') }, ...detail.goals].map(
                          (g) => (
                            <button
                              key={g._id ?? 'general'}
                              type="button"
                              onClick={() => setThread(g._id)}
                              className={cn(
                                'max-w-48 cursor-pointer truncate rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                                thread === g._id
                                  ? 'border-primary/40 bg-primary/10 text-primary-text'
                                  : 'text-muted-foreground hover:text-foreground',
                              )}
                            >
                              {g.title}
                            </button>
                          ),
                        )}
                      </div>
                      <NotesThread
                        key={thread ?? 'general'}
                        linkId={detail.linkId}
                        goalId={thread}
                        viewerIsCoach
                        coachName={me?.name ?? ''}
                        clientName={detail.client.name}
                        composerTools={(fill) => (
                          <AssistantDraft clientId={clientId} goalId={thread} onDraft={fill} />
                        )}
                        className="mt-3"
                      />
                      {thread === null && (
                        <p className="mt-2 text-xs text-muted-foreground">
                          {t('coaching.notes.pinHint')}
                        </p>
                      )}
                    </section>
                    <button
                      type="button"
                      onClick={() => setConfirmEnd(true)}
                      className="cursor-pointer text-sm font-medium text-destructive hover:underline"
                    >
                      {t('coaching.settings.end')}
                    </button>
                  </div>
                </div>

                <AssistantTweaksDialog
                  clientId={clientId}
                  clientName={detail.client.name}
                  goal={tweakGoal}
                  onClose={() => setTweakGoal(null)}
                />

                <GivePlanDialog
                  clientId={clientId}
                  clientName={detail.client.name}
                  goals={detail.goals}
                  open={givePlan}
                  onOpenChange={setGivePlan}
                />

                <AlertDialog open={confirmEnd} onOpenChange={setConfirmEnd}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('coaching.settings.endTitle', { name: detail.client.name })}
                      </AlertDialogTitle>
                      <AlertDialogDescription>{t('coaching.endCoachDesc')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        variant="destructive"
                        onClick={() => {
                          setConfirmEnd(false)
                          void end({ linkId: detail.linkId })
                            .then(() => navigate({ to: '/coaching' }))
                            .catch((err) => toastCoachError(err, t))
                        }}
                      >
                        {t('coaching.settings.end')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
